import React, { useState, useEffect } from 'react';
import data from '../data.json';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  /* Scroll → navbar background */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* IntersectionObserver → active nav link */
  useEffect(() => {
    const sectionIds = data.nav.map(n => n.id);
    const observers = [];
    sectionIds.forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection(id); },
        { rootMargin: '-40% 0px -55% 0px' }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, []);

  const toggle = () => setMenuOpen(p => !p);
  const close  = () => setMenuOpen(false);

  const navStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
    transition: 'all 0.32s ease',
    background: scrolled ? 'rgba(6,6,15,0.94)' : 'transparent',
    backdropFilter: scrolled ? 'blur(20px)' : 'none',
    WebkitBackdropFilter: scrolled ? 'blur(20px)' : 'none',
    borderBottom: scrolled ? '1px solid rgba(255,255,255,0.05)' : 'none',
  };

  return (
    <nav style={navStyle}>
      <div className="container">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 70 }}>

          {/* Logo */}
          <a href="#hero" style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)',
              flexShrink: 0, animation: 'pulseRing 2.2s infinite', display: 'block',
            }} />
            <span style={{ fontFamily: 'var(--font)', fontWeight: 900, fontSize: '1.45rem', letterSpacing: '-0.02em' }}>
              Vy<span style={{ color: 'var(--accent)' }}>linx</span>
            </span>
          </a>

          {/* Desktop links */}
          <ul style={{ display: 'flex', gap: 4, listStyle: 'none', alignItems: 'center' }} className="desktop-nav">
            {data.nav.map(item => {
              const isActive = activeSection === item.id;
              return (
                <li key={item.id}>
                  <a
                    href={item.href}
                    style={{
                      color: isActive ? '#fff' : 'rgba(255,255,255,0.6)',
                      fontSize: 13.5,
                      fontWeight: 600,
                      padding: '8px 14px',
                      borderRadius: 6,
                      background: isActive ? 'rgba(255,255,255,0.07)' : 'transparent',
                      transition: 'all var(--ease)',
                      position: 'relative',
                      display: 'block',
                    }}
                    onMouseEnter={e => { if (!isActive) { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}}
                    onMouseLeave={e => { if (!isActive) { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; e.currentTarget.style.background = 'transparent'; }}}
                  >
                    {item.label}
                    {isActive && (
                      <span style={{
                        position: 'absolute', bottom: 4, left: '50%', transform: 'translateX(-50%)',
                        width: 16, height: 2, background: 'var(--accent)', borderRadius: 2, display: 'block',
                      }} />
                    )}
                  </a>
                </li>
              );
            })}
          </ul>

          {/* CTA + Hamburger */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <a href="#contact" className="btn-primary" style={{ padding: '10px 20px', fontSize: 13 }}>
              Get Started
            </a>
            <button
              onClick={toggle}
              aria-label="Toggle menu"
              style={{
                display: 'none', flexDirection: 'column', gap: 5,
                background: 'none', border: 'none', cursor: 'pointer', padding: 4,
              }}
              className="hamburger"
            >
              {[0, 1, 2].map(i => (
                <span key={i} style={{
                  display: 'block', width: 21, height: 2,
                  background: '#fff', borderRadius: 2,
                  transition: 'all 0.3s',
                  transform: menuOpen
                    ? (i === 0 ? 'rotate(45deg) translate(5px,5px)'
                      : i === 2 ? 'rotate(-45deg) translate(5px,-5px)' : 'none')
                    : 'none',
                  opacity: menuOpen && i === 1 ? 0 : 1,
                }} />
              ))}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div style={{
          position: 'fixed', top: 70, left: 0, right: 0,
          background: 'rgba(6,6,15,0.97)', backdropFilter: 'blur(20px)',
          padding: '22px 28px', borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', flexDirection: 'column', gap: 2, zIndex: 998,
        }}>
          {data.nav.map(item => (
            <a
              key={item.id}
              href={item.href}
              onClick={close}
              style={{
                color: activeSection === item.id ? 'var(--accent)' : 'rgba(255,255,255,0.8)',
                fontSize: 16, fontWeight: 600,
                padding: '12px 0',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                display: 'block',
              }}
            >
              {item.label}
            </a>
          ))}
        </div>
      )}

      <style>{`
        @media (max-width: 820px) {
          .desktop-nav { display: none !important; }
          .hamburger { display: flex !important; }
        }
      `}</style>
    </nav>
  );
}
